import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card, Form, Button, Alert, Row, Col, Spinner, Badge, Table, Modal, InputGroup
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus, faTrash, faPhone, faVideo, faFileAudio, faUpload, faSpinner
} from "@fortawesome/free-solid-svg-icons";
import api from "../../utils/api";
import { uploadToR2, sanitizeStorageFileName } from "../../utils/r2Upload";

const SKIP_LABELS = {
  no_user: "Üye yok",
  no_fcm: "Uygulama silinmiş / token yok",
  not_target: "Hedef değil",
  all_chatted: "Kümedeki herkesle sohbet var",
  empty_cluster: "Küme boş",
  disabled: "Sistem kapalıydı",
  busy: "Üye aramadaydı",
  place_failed: "Arama başlatılamadı"
};

const STATUS_VARIANT = {
  pending: "secondary",
  processing: "info",
  called: "success",
  skipped: "warning",
  failed: "danger"
};

export default () => {
  const [settings, setSettings] = useState({ enabled: false, delayMin: 30, cluster: [] });
  const [fakes, setFakes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showFakeModal, setShowFakeModal] = useState(false);
  const [fakeSearch, setFakeSearch] = useState("");
  const [addingUid, setAddingUid] = useState(null);
  const [recordingCache, setRecordingCache] = useState({});
  const [uploadTarget, setUploadTarget] = useState(null);
  const [recordingTitle, setRecordingTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [sRes, fRes, jRes] = await Promise.all([
        api.get("/auto-call/settings"),
        api.get("/auto-call/fakes"),
        api.get("/auto-call/jobs?limit=40")
      ]);
      const nextSettings = sRes.data?.settings || { enabled: false, delayMin: 30, cluster: [] };
      setSettings(nextSettings);
      setFakes(fRes.data?.users || []);
      setJobs(jRes.data?.jobs || []);
      const uids = [...new Set((nextSettings.cluster || []).map((m) => m.fakeUid).filter(Boolean))];
      await Promise.all(uids.map((uid) => loadRecordings(uid, true)));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadRecordings = async (uid, force = false) => {
    if (!uid) return [];
    if (!force && recordingCache[uid]) return recordingCache[uid];
    try {
      const res = await api.get(`/users/${uid}/recordings`);
      const combined = Array.isArray(res.data?.recordings) ? res.data.recordings : [];
      const audio = res.data?.audioRecordings || [];
      const video = res.data?.videoRecordings || [];
      const seen = new Set();
      const list = [...combined, ...audio, ...video].filter((r) => {
        if (!r?.id || seen.has(r.id)) return false;
        seen.add(r.id);
        return true;
      });
      setRecordingCache((prev) => ({ ...prev, [uid]: list }));
      return list;
    } catch (e) {
      return recordingCache[uid] || [];
    }
  };

  const closeUploadModal = () => {
    setUploadTarget(null);
    setRecordingTitle("");
    setSelectedFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isAudio = file.type.startsWith("audio/");
    const isVideo = file.type.startsWith("video/");
    if (!isAudio && !isVideo) {
      setError("Ses (MP3, M4A) veya video (MP4) dosyası seçin.");
      return;
    }
    setSelectedFile(file);
    setError(null);
    if (!recordingTitle.trim()) {
      setRecordingTitle(file.name.replace(/\.[^.]+$/, ""));
    }
  };

  const handleUpload = async () => {
    if (!uploadTarget?.uid || !selectedFile) return;
    try {
      setUploading(true);
      setError(null);
      const mediaType = selectedFile.type.startsWith("video/") ? "video" : "audio";
      const fileName = sanitizeStorageFileName(selectedFile.name);
      const storagePath = `test_users/${uploadTarget.uid}/${mediaType}/${fileName}`;
      const downloadUrl = await uploadToR2(selectedFile, storagePath);
      const res = await api.post(`/users/${uploadTarget.uid}/recordings`, {
        type: mediaType,
        title: recordingTitle.trim() || selectedFile.name,
        fileUrl: downloadUrl,
        fileName,
        fileSize: selectedFile.size
      });
      const rec = res.data?.recording;
      await loadRecordings(uploadTarget.uid, true);
      if (rec) {
        const cluster = [...(settings.cluster || [])];
        cluster[uploadTarget.index] = {
          ...cluster[uploadTarget.index],
          recordingId: rec.id,
          recordingTitle: rec.title,
          mediaUrl: rec.fileUrl,
          mediaType: rec.type === "video" ? "video" : "audio"
        };
        const next = { ...settings, cluster };
        setSettings(next);
        await api.put("/auto-call/settings", next);
      }
      setSuccess(`${uploadTarget.name || "Hesap"} kaydı eklendi. Aynı hesabın ses/video kayıtlarında da görünür.`);
      setTimeout(() => setSuccess(null), 4000);
      closeUploadModal();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Kayıt yüklenemedi.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      const res = await api.put("/auto-call/settings", settings);
      setSettings(res.data.settings);
      setSuccess("Ayarlar kaydedildi. Yeni üyeler bu küme ve süreyle kuyruğa alınır.");
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const usedUids = new Set((settings.cluster || []).map((c) => c.fakeUid));
  const filteredFakes = fakes.filter((f) => {
    if (usedUids.has(f.uid)) return false;
    if (!fakeSearch.trim()) return true;
    return (f.name || "").toLowerCase().includes(fakeSearch.toLowerCase().trim());
  });

  const addFake = async (fake) => {
    if ((settings.cluster || []).length >= 10) return;
    setAddingUid(fake.uid);
    const recs = await loadRecordings(fake.uid, true);
    const first = recs[0];
    setSettings((prev) => ({
      ...prev,
      cluster: [
        ...(prev.cluster || []),
        {
          fakeUid: fake.uid,
          fakeName: fake.name,
          recordingId: first ? first.id : null,
          recordingTitle: first ? first.title : "",
          mediaUrl: first ? first.fileUrl : "",
          mediaType: first && first.type === "video" ? "video" : "audio"
        }
      ]
    }));
    setAddingUid(null);
    setShowFakeModal(false);
    setFakeSearch("");
  };

  const updateMember = (index, rec) => {
    setSettings((prev) => {
      const cluster = [...(prev.cluster || [])];
      cluster[index] = {
        ...cluster[index],
        recordingId: rec.id,
        recordingTitle: rec.title,
        mediaUrl: rec.fileUrl,
        mediaType: rec.type === "video" ? "video" : "audio"
      };
      return { ...prev, cluster };
    });
  };

  const removeMember = (index) => {
    setSettings((prev) => ({
      ...prev,
      cluster: (prev.cluster || []).filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    (settings.cluster || []).forEach((m) => {
      if (m.fakeUid) loadRecordings(m.fakeUid);
    });
  }, [settings.cluster]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="py-4">
      <h4 className="mb-1">Toplu Arama Menüsü</h4>
      <p className="text-muted mb-4">
        Yeni üye kaydından sonra beklenir. Süre dolunca kümedeki, o üyeyle henüz sohbeti olmayan
        hesaplardan biri rastgele arar. Ses dosyası sesli, video dosyası görüntülü arama başlatır.
        Uygulaması silinmiş (FCM token yok) üyelere arama gitmez.
      </p>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Form.Check
            type="switch"
            id="auto-call-enabled"
            label="Otomatik kayıtlı arama aktif"
            checked={!!settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            className="mb-3"
          />
          <Row>
            <Col md={4}>
              <Form.Label>Kayıt sonrası bekleme (dk)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={settings.delayMin ?? 30}
                onChange={(e) => setSettings({ ...settings, delayMin: Number(e.target.value) })}
              />
              <Form.Text className="text-muted">Varsayılan 30 dakika.</Form.Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="mb-0">Arama kümesi</h6>
              <small className="text-muted">En fazla 10 fake hesap. Kayıt buradan veya hesabın ses/video sayfasından eklenir; aynı kütüphanedir.</small>
            </div>
            <Button
              size="sm"
              variant="primary"
              disabled={(settings.cluster || []).length >= 10}
              onClick={() => setShowFakeModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Hesap ekle
            </Button>
          </div>

          {(settings.cluster || []).length === 0 && (
            <Alert variant="light" className="mb-0">Henüz hesap eklenmedi. Sistem açık olsa bile küme boşsa arama yapılmaz.</Alert>
          )}

          {(settings.cluster || []).map((member, index) => {
            const recs = recordingCache[member.fakeUid] || [];
            const isVideo = member.mediaType === "video";
            return (
              <div key={member.fakeUid} className="border rounded p-3 mb-2">
                <Row className="align-items-center">
                  <Col md={3}>
                    <div className="fw-semibold">{member.fakeName || member.fakeUid}</div>
                    <small className="text-muted d-block">{member.fakeUid}</small>
                    <Link
                      to={`/accounts/recordings/${member.fakeUid}?type=${isVideo ? "video" : "audio"}`}
                      className="small"
                    >
                      Tüm kayıtlar
                    </Link>
                  </Col>
                  <Col md={5}>
                    <InputGroup>
                      <Form.Select
                        value={member.recordingId || ""}
                        onChange={(e) => {
                          const rec = recs.find((r) => r.id === e.target.value);
                          if (rec) updateMember(index, rec);
                        }}
                      >
                        {!recs.length && <option value="">Kayıt yok — buradan ekleyin</option>}
                        {recs.length > 0 && !member.recordingId && (
                          <option value="">Kayıt seçin</option>
                        )}
                        {recs.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.type === "video" ? "Video" : "Ses"} — {r.title || r.id}
                          </option>
                        ))}
                      </Form.Select>
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          setError(null);
                          setRecordingTitle("");
                          setSelectedFile(null);
                          setUploadTarget({
                            uid: member.fakeUid,
                            name: member.fakeName || member.fakeUid,
                            index
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faUpload} className="me-1" />
                        Kayıt ekle
                      </Button>
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Badge bg={isVideo ? "danger" : "info"}>
                      <FontAwesomeIcon icon={isVideo ? faVideo : faFileAudio} className="me-1" />
                      {isVideo ? "Görüntülü" : "Sesli"}
                    </Badge>
                  </Col>
                  <Col md={1} className="text-end">
                    <Button variant="outline-danger" size="sm" onClick={() => removeMember(index)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </Col>
                </Row>
                {!member.mediaUrl && (
                  <small className="text-danger d-block mt-2">Bu hesap seçilemez: dosya yok.</small>
                )}
              </div>
            );
          })}

          <Button variant="primary" className="mt-3" onClick={save} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Son işlemler</h6>
            <Button size="sm" variant="outline-secondary" onClick={load}>Yenile</Button>
          </div>
          <Table responsive hover className="align-middle">
            <thead>
              <tr>
                <th>Üye</th>
                <th>Durum</th>
                <th>Arayan</th>
                <th>Tip</th>
                <th>Zaman</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr><td colSpan={5} className="text-muted">Henüz kayıt yok.</td></tr>
              )}
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td><code>{(j.userId || "").slice(0, 12)}</code></td>
                  <td>
                    <Badge bg={STATUS_VARIANT[j.status] || "secondary"}>{j.status}</Badge>
                    {j.skipReason && (
                      <div className="small text-muted">{SKIP_LABELS[j.skipReason] || j.skipReason}</div>
                    )}
                  </td>
                  <td>{j.callerName || j.callerId || "-"}</td>
                  <td>
                    {j.callType === "video" ? "Görüntülü" : j.callType === "voice" ? "Sesli" : "-"}
                  </td>
                  <td className="small">
                    {j.processedAt
                      ? new Date(j.processedAt).toLocaleString("tr-TR")
                      : j.eligibleAt
                        ? `Bekliyor: ${new Date(j.eligibleAt).toLocaleString("tr-TR")}`
                        : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showFakeModal} onHide={() => setShowFakeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Küme hesabı seç</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-3">
            <Form.Control
              placeholder="İsim ara..."
              value={fakeSearch}
              onChange={(e) => setFakeSearch(e.target.value)}
            />
          </InputGroup>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {filteredFakes.length === 0 && <div className="text-muted">Eklenecek hesap kalmadı.</div>}
            {filteredFakes.map((f) => (
              <button
                key={f.uid}
                type="button"
                className="d-flex w-100 align-items-center border-0 border-bottom bg-transparent py-2 px-1 text-start"
                disabled={addingUid === f.uid}
                onClick={() => addFake(f)}
              >
                {f.profileImageUrl ? (
                  <img
                    src={f.profileImageUrl}
                    alt=""
                    width={36}
                    height={36}
                    className="rounded-circle me-2"
                    style={{ objectFit: "cover" }}
                  />
                ) : (
                  <span className="me-2"><FontAwesomeIcon icon={faPhone} /></span>
                )}
                <span>{f.name}</span>
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={!!uploadTarget} onHide={closeUploadModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            Kayıt ekle{uploadTarget?.name ? ` — ${uploadTarget.name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Bu dosya hesabın ortak kayıt kütüphanesine yazılır. Buradan veya ses/video kayıt
            sayfasından eklenen her kayıt iki yerde de görünür. Ses dosyası sesli, video dosyası
            görüntülü arama başlatır.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Başlık</Form.Label>
            <Form.Control
              type="text"
              value={recordingTitle}
              onChange={(e) => setRecordingTitle(e.target.value)}
              placeholder="Örn: Selamlaşma"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Ses veya video dosyası</Form.Label>
            <Form.Control
              type="file"
              accept="audio/*,video/*"
              onChange={handleFileSelect}
            />
            {selectedFile && (
              <small className="text-muted d-block mt-2">
                {selectedFile.type.startsWith("video/") ? "Video" : "Ses"} — {selectedFile.name}
              </small>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeUploadModal} disabled={uploading}>
            İptal
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Yükleniyor...
              </>
            ) : (
              "Yükle"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
